// @ts-check
import { useState } from 'react';
import Modal from './Modal';

/**
 * Confirmation step for a bulk action. With `requireCount` (use it for anything destructive)
 * the admin must type the number of affected rows before the confirm button unlocks.
 * Extra options (e.g. a "Notify customers" checkbox) go in as children.
 * @param {{
 *   title: string,
 *   message?: string,
 *   count: number,
 *   confirmLabel?: string,
 *   danger?: boolean,
 *   requireCount?: boolean,
 *   busy?: boolean,
 *   onConfirm: () => void,
 *   onClose: () => void,
 *   children?: React.ReactNode,
 * }} props
 */
const BulkConfirmModal = ({
  title,
  message,
  count,
  confirmLabel = 'Confirm',
  danger = false,
  requireCount = false,
  busy = false,
  onConfirm,
  onClose,
  children,
}) => {
  const [typed, setTyped] = useState('');
  const unlocked = !requireCount || typed.trim() === String(count);

  return (
    <Modal onClose={busy ? undefined : onClose} maxWidth="md" fullHeight={false}>
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">
          {message || `This will affect ${count} selected item${count === 1 ? '' : 's'}.`}
        </p>
        {children}
        {requireCount && (
          <label className="block text-sm text-foreground/80">
            Type <span className="font-semibold">{count}</span> to confirm
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-1 w-full border border-input bg-background rounded-lg px-3 py-2 text-sm"
            />
          </label>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-accent disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!unlocked || busy}
            className={`px-4 py-2 text-sm rounded-lg disabled:opacity-50 ${
              danger
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {busy ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkConfirmModal;
